import React from 'react';
import WebhookDocs from './WebhookDocs.jsx';

const docSections = [
    {
        id: 'overview',
        label: 'Resumen',
        title: 'Webhooks de Exppedify',
        render: () => (
            <>
                <p className="text-muted">
                    Los webhooks permiten que Exppedify avise a aplicaciones externas cuando ocurre un evento dentro de la plataforma.
                    La app externa registra una o más URLs y Exppedify envía un POST a cada URL activa suscrita al evento.
                </p>
                <div className="bg-light rounded p-4">
                    <div className="font-weight-bold mb-2">Implementado actualmente</div>
                    <ul className="text-muted pl-5 mb-0">
                        <li>Múltiples URLs activas por empresa</li>
                        <li>Selección de eventos por URL</li>
                        <li>Firma HMAC SHA-256 por URL usando secret generado por Exppedify</li>
                        <li>Bitácora de entregas y reenvío manual</li>
                        <li>Pruebas manuales de cambio de estatus de expediente</li>
                    </ul>
                </div>
            </>
        ),
    },
    {
        id: 'configuration',
        label: 'Alta de URLs',
        title: 'Cómo configurar endpoints',
        render: () => (
            <>
                <ol className="text-muted pl-5">
                    <li>Entra a Developers y abre Configuración.</li>
                    <li>Agrega una URL HTTPS que reciba peticiones POST.</li>
                    <li>Haz clic en generar para que Exppedify cree el secret y cópialo en tu app receptora.</li>
                    <li>Marca los eventos que esa URL quiere recibir.</li>
                    <li>Guarda la URL. Puedes repetir el proceso para registrar más destinos.</li>
                </ol>
                <p className="text-muted mb-0">
                    Si guardas una URL que ya existía, Exppedify actualiza su secret y eventos. Si desactivas una URL, deja de recibir eventos nuevos, pero sus entregas anteriores permanecen en la bitácora.
                </p>
            </>
        ),
    },
    {
        id: 'events',
        label: 'Eventos',
        title: 'Eventos soportados',
        render: () => (
            <div className="card card-custom border mb-0">
                <div className="card-body">
                    <div className="mb-5">
                        <div className="font-weight-bold mb-2">
                            <code>person_provider.status_changed</code>
                        </div>
                        <p className="text-muted mb-0">
                            Se envía cuando un expediente de proveedor cambia realmente su estatus. También puede enviarse manualmente desde la pantalla de configuración para probar integraciones.
                        </p>
                    </div>
                    <div>
                        <div className="font-weight-bold mb-2">
                            <code>people.updated</code>
                        </div>
                        <p className="text-muted mb-0">
                            Se envía cuando cambia cualquier campo de un registro People. La entrega llega a la empresa actualizada y a las empresas que tengan a ese People como proveedor.
                        </p>
                    </div>
                </div>
            </div>
        ),
    },
    {
        id: 'payload',
        label: 'Payload',
        title: 'Formato del payload',
        render: () => (
            <>
                <p className="text-muted">
                    Todos los webhooks usan una estructura estándar con identificador del evento, tipo, fecha y datos del evento.
                </p>
                <pre className="bg-light rounded p-4 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{`{
  "event_id": "evt_01kxkn3ge3mrq2q2pgagnr4msk",
  "event_type": "person_provider.status_changed",
  "occurred_at": "2026-07-15T19:50:59+00:00",
  "data": {
    "person_provider_id": 1,
    "people_id": 2,
    "persona_provider_id": 2,
    "dossier_id": 2,
    "previous_status": {
      "id": 4,
      "name": "Completo"
    },
    "current_status": {
      "id": 5,
      "name": "Vigente"
    },
    "provider": {
      "id": 2,
      "rfc": "AAAA810202RR5",
      "fiscal_name": "Proveedor Demo"
    },
    "dossier": {
      "id": 2,
      "title": "Expediente proveedor"
    }
  }
}`}</pre>
                <pre className="bg-light rounded p-4 mt-5 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{`{
  "event_id": "evt_01kxkn3ge3mrq2q2pgagnr4msk",
  "event_type": "people.updated",
  "occurred_at": "2026-07-18T12:00:00+00:00",
  "data": {
    "people_id": 2,
    "changed_fields": ["fiscal_name", "rfc", "updated_at"],
    "previous_values": {
      "fiscal_name": "Proveedor Demo SA de CV",
      "rfc": "AAAA810202RR5",
      "updated_at": "2026-07-18T11:58:00+00:00"
    },
    "current_values": {
      "fiscal_name": "Proveedor Demo Actualizado SA de CV",
      "rfc": "AAAA810202RR6",
      "updated_at": "2026-07-18T12:00:00+00:00"
    },
    "person": {
      "id": 2,
      "name": "Proveedor Demo",
      "person_type": "Moral",
      "rfc": "AAAA810202RR6",
      "fiscal_name": "Proveedor Demo Actualizado SA de CV",
      "zipcode": "64000",
      "municipality_id": 1,
      "country_id": 1,
      "regimen_id": 601,
      "updated_at": "2026-07-18T12:00:00+00:00"
    }
  }
}`}</pre>
            </>
        ),
    },
    {
        id: 'headers',
        label: 'Headers',
        title: 'Headers enviados',
        render: () => (
            <>
                <p className="text-muted">Cada entrega incluye headers para identificar el evento, la entrega y la firma.</p>
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead>
                            <tr>
                                <th>Header</th>
                                <th>Significado</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>X-Exppedify-Event</code></td>
                                <td>Tipo de evento enviado.</td>
                            </tr>
                            <tr>
                                <td><code>X-Exppedify-Delivery</code></td>
                                <td>Identificador único de la entrega HTTP.</td>
                            </tr>
                            <tr>
                                <td><code>X-Exppedify-Timestamp</code></td>
                                <td>Timestamp usado para firmar el cuerpo de la petición.</td>
                            </tr>
                            <tr>
                                <td><code>X-Exppedify-Signature</code></td>
                                <td>Firma HMAC SHA-256 del payload.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </>
        ),
    },
    {
        id: 'signature',
        label: 'Secret y firma',
        title: 'Qué es el secret de firma',
        render: () => (
            <>
                <p className="text-muted">
                    El secret de firma es una llave compartida entre Exppedify y la aplicación que recibe el webhook. No es una contraseña de usuario.
                    Sirve para validar que el webhook fue generado por Exppedify y que el cuerpo de la petición no cambió en tránsito.
                </p>
                <div className="bg-light rounded p-4 mb-4">
                    <div className="font-weight-bold mb-2">Cómo se calcula</div>
                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{`signed_payload = timestamp + "." + raw_body
signature = "sha256=" + HMAC_SHA256(signed_payload, secret)`}</pre>
                </div>
                <p className="text-muted mb-0">
                    La app receptora debe recalcular esa firma con el mismo secret y compararla contra <code>X-Exppedify-Signature</code>. Si no coincide, debe rechazar o ignorar el webhook.
                </p>
            </>
        ),
    },
    {
        id: 'receiver',
        label: 'App receptora',
        title: 'Cómo recibir webhooks',
        render: () => (
            <>
                <p className="text-muted">Tu aplicación debe exponer un endpoint HTTPS que acepte POST y responda rápido con un código 2xx.</p>
                <pre className="bg-light rounded p-4 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{`app.post('/webhooks/exppedify', express.json(), (req, res) => {
  const eventType = req.header('X-Exppedify-Event');
  const deliveryId = req.header('X-Exppedify-Delivery');
  const signature = req.header('X-Exppedify-Signature');

  // 1. Validar firma.
  // 2. Procesar segun event_type.
  // 3. Guardar event_id para evitar procesar duplicados.

  res.status(200).json({ ok: true });
});`}</pre>
            </>
        ),
    },
    {
        id: 'deliveries',
        label: 'Entregas',
        title: 'Bitácora, reintentos y reenvíos',
        render: () => (
            <>
                <p className="text-muted">
                    Cada envío queda registrado como una entrega. Si hay tres URLs activas para el mismo evento, Exppedify crea tres entregas independientes.
                </p>
                <ul className="text-muted pl-5 mb-0">
                    <li>Una respuesta HTTP 2xx se considera exitosa.</li>
                    <li>Un error o timeout se registra como fallo después de la estrategia de entrega configurada.</li>
                    <li>Desde la bitácora puedes reenviar una entrega específica.</li>
                    <li>La app receptora debe usar <code>event_id</code> para evitar procesar duplicados.</li>
                </ul>
            </>
        ),
    },
    {
        id: 'testing',
        label: 'Pruebas',
        title: 'Cómo probar una integración',
        render: () => (
            <>
                <ol className="text-muted pl-5 mb-0">
                    <li>Levanta una app receptora local o usa una URL pública temporal.</li>
                    <li>Expón esa app con ngrok u otro túnel HTTPS.</li>
                    <li>Registra la URL en Configuración y selecciona el evento.</li>
                    <li>Usa los botones de estatus para enviar un webhook manual.</li>
                    <li>Revisa la bitácora en Exppedify y los logs de tu app receptora.</li>
                </ol>
            </>
        ),
    },
];

export default class Developers extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            activeProduct: null,
            activeTool: null,
            activeDocSection: 'overview',
        };
    }

    openProduct = product => {
        this.setState({
            activeProduct: product,
            activeTool: null,
        });
    };

    openTool = tool => {
        this.setState({ activeTool: tool });
    };

    setDocSection = sectionId => {
        this.setState({ activeDocSection: sectionId });
    };

    renderMenu = () => (
        <div className="row">
            <div className="col-lg-6 mb-6">
                <div className="card card-custom h-100 border">
                    <div className="card-body d-flex flex-column">
                        <div className="d-flex align-items-center mb-5">
                            <span className="symbol symbol-50 symbol-light-secondary mr-4">
                                <span className="symbol-label">
                                    <i className="flaticon2-lock text-muted icon-2x"></i>
                                </span>
                            </span>
                            <div>
                                <h3 className="font-weight-bold mb-1 text-dark">API</h3>
                                <span className="label label-light-secondary label-inline">Próximamente</span>
                            </div>
                        </div>
                        <p className="text-muted mb-0">
                            Documentación y configuración de API para integraciones directas.
                        </p>
                        <div className="row mt-6">
                            <div className="col-sm-6 mb-3 mb-sm-0">
                                <button type="button" className="btn btn-light-secondary btn-block" disabled>
                                    Configuración
                                </button>
                            </div>
                            <div className="col-sm-6">
                                <button type="button" className="btn btn-light-secondary btn-block" disabled>
                                    Documentación
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-lg-6 mb-6">
                <div className="card card-custom h-100 border">
                    <div className="card-body d-flex flex-column">
                        <div className="d-flex align-items-center mb-5">
                            <span className="symbol symbol-50 symbol-light-primary mr-4">
                                <span className="symbol-label">
                                    <i className="flaticon2-send text-primary icon-2x"></i>
                                </span>
                            </span>
                            <div>
                                <h3 className="font-weight-bold mb-1 text-dark">Webhooks</h3>
                                <span className="label label-light-primary label-inline">Disponible</span>
                            </div>
                        </div>
                        <p className="text-muted mb-0">
                            Configura URLs destino, eventos, firmas, pruebas y entregas.
                        </p>
                        <div className="row mt-6">
                            <div className="col-sm-6 mb-3 mb-sm-0">
                                <button
                                    type="button"
                                    className="btn btn-primary btn-block"
                                    onClick={() => this.setState({ activeProduct: 'webhooks', activeTool: 'configuration' })}
                                >
                                    Configuración
                                </button>
                            </div>
                            <div className="col-sm-6">
                                <button
                                    type="button"
                                    className="btn btn-light-info btn-block"
                                    onClick={() => this.setState({ activeProduct: 'webhooks', activeTool: 'documentation' })}
                                >
                                    Documentación
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    renderHeader = (title, subtitle) => (
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-6">
            <div>
                <h3 className="font-weight-bold mb-1">{title}</h3>
                <p className="text-muted mb-0">{subtitle}</p>
            </div>
            <button
                type="button"
                className="btn btn-light-primary mt-3 mt-md-0"
                onClick={() => this.openProduct(null)}
            >
                Volver a Developers
            </button>
        </div>
    );

    renderProductMenu = (product, title, subtitle, apiIsComingSoon = false) => (
        <>
            {this.renderHeader(title, subtitle)}
            <div className="row">
                <div className="col-lg-6 mb-6">
                    <button
                        type="button"
                        className="card card-custom h-100 border text-left w-100 btn btn-clean p-0"
                        onClick={() => !apiIsComingSoon && this.openTool('configuration')}
                    >
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-4">
                                <span className={`symbol symbol-50 ${apiIsComingSoon ? 'symbol-light-secondary' : 'symbol-light-primary'} mr-4`}>
                                    <span className="symbol-label">
                                        <i className={`flaticon2-settings ${apiIsComingSoon ? 'text-muted' : 'text-primary'} icon-2x`}></i>
                                    </span>
                                </span>
                                <div>
                                    <h3 className="font-weight-bold mb-1 text-dark">Configuración</h3>
                                    <span className={`label ${apiIsComingSoon ? 'label-light-secondary' : 'label-light-primary'} label-inline`}>
                                        {apiIsComingSoon ? 'Próximamente' : product}
                                    </span>
                                </div>
                            </div>
                            <p className="text-muted mb-0">
                                {apiIsComingSoon
                                    ? 'Configuración de credenciales, permisos y endpoints de API.'
                                    : 'Alta de URLs, selección de eventos, generación de secret, pruebas y bitácora.'}
                            </p>
                        </div>
                    </button>
                </div>
                <div className="col-lg-6 mb-6">
                    <button
                        type="button"
                        className="card card-custom h-100 border text-left w-100 btn btn-clean p-0"
                        onClick={() => !apiIsComingSoon && this.openTool('documentation')}
                    >
                        <div className="card-body">
                            <div className="d-flex align-items-center mb-4">
                                <span className={`symbol symbol-50 ${apiIsComingSoon ? 'symbol-light-secondary' : 'symbol-light-info'} mr-4`}>
                                    <span className="symbol-label">
                                        <i className={`flaticon2-browser ${apiIsComingSoon ? 'text-muted' : 'text-info'} icon-2x`}></i>
                                    </span>
                                </span>
                                <div>
                                    <h3 className="font-weight-bold mb-1 text-dark">Documentación</h3>
                                    <span className={`label ${apiIsComingSoon ? 'label-light-secondary' : 'label-light-info'} label-inline`}>
                                        {apiIsComingSoon ? 'Próximamente' : 'Guía técnica'}
                                    </span>
                                </div>
                            </div>
                            <p className="text-muted mb-0">
                                {apiIsComingSoon
                                    ? 'Guías técnicas para consumir la API de Exppedify.'
                                    : 'Cómo integrar, validar firmas, entender payloads y operar entregas.'}
                            </p>
                        </div>
                    </button>
                </div>
            </div>
        </>
    );

    renderWebhooksToolNav = () => (
        <div className="row mb-6">
            <div className="col-lg-6 mb-4 mb-lg-0">
                <button
                    type="button"
                    className={`btn btn-block text-left p-5 ${this.state.activeTool === 'configuration' ? 'btn-primary' : 'btn-light-primary'}`}
                    onClick={() => this.openTool('configuration')}
                >
                    <div className="font-weight-bold">Configuración</div>
                    <div className={this.state.activeTool === 'configuration' ? 'text-white-50' : 'text-muted'}>
                        URLs destino, eventos, generación de secret, pruebas y bitácora.
                    </div>
                </button>
            </div>
            <div className="col-lg-6">
                <button
                    type="button"
                    className={`btn btn-block text-left p-5 ${this.state.activeTool === 'documentation' ? 'btn-info' : 'btn-light-info'}`}
                    onClick={() => this.openTool('documentation')}
                >
                    <div className="font-weight-bold">Documentación</div>
                    <div className={this.state.activeTool === 'documentation' ? 'text-white-50' : 'text-muted'}>
                        Guía técnica, payloads, headers, firma y recepción.
                    </div>
                </button>
            </div>
        </div>
    );

    renderWebhooksConfiguration = () => (
        <>
            {this.renderHeader('Configuración de webhooks', 'Administra URLs, eventos, pruebas y entregas.')}
            {this.renderWebhooksToolNav()}
            <WebhookDocs />
        </>
    );

    renderDocumentation = () => {
        const active = docSections.find(section => section.id === this.state.activeDocSection) || docSections[0];

        return (
            <>
                {this.renderHeader('Documentación de webhooks', 'Guía técnica para integrar aplicaciones externas con Exppedify.')}
                {this.renderWebhooksToolNav()}
                <div className="row">
                    <div className="col-lg-3 mb-6">
                        <div className="card card-custom">
                            <div className="card-body">
                                {docSections.map(section => {
                                    const isActive = section.id === active.id;

                                    return (
                                        <button
                                            key={section.id}
                                            type="button"
                                            className={`btn btn-block text-left mb-2 ${isActive ? 'btn-primary' : 'btn-light-primary'}`}
                                            onClick={() => this.setDocSection(section.id)}
                                        >
                                            {section.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-9">
                        <div className="card card-custom">
                            <div className="card-body">
                                <h3 className="font-weight-bold mb-5">{active.title}</h3>
                                {active.render()}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    render() {
        if (this.state.activeProduct === 'webhooks' && this.state.activeTool === 'configuration') {
            return this.renderWebhooksConfiguration();
        }

        if (this.state.activeProduct === 'webhooks' && this.state.activeTool === 'documentation') {
            return this.renderDocumentation();
        }

        return this.renderMenu();
    }
}
